import { useState, useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { useAuth } from '../lib/auth-context';
import { FASTAPI_URL } from '@/lib/config';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from './ui/card';

interface SiteflowConfigData {
  server_url: string | null;
  client_id: string | null;
  client_secret: string | null;
}

const SiteflowConfig = () => {
  const [siteflowConfig, setSiteflowConfig] = useState<SiteflowConfigData>({
    server_url: '',
    client_id: '',
    client_secret: '',
  });
  const [isLoadingConfig, setIsLoadingConfig] = useState(true);
  const [configError, setConfigError] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<string | null>(null);

  const { user } = useAuth();

  useEffect(() => {
    const fetchSiteflowConfig = async () => {
      if (!user?.token || !user?._id) {
        setIsLoadingConfig(false);
        setConfigError('You must be logged in to view your configuration.');
        return;
      }

      setIsLoadingConfig(true);
      setConfigError(null);
      setSaveStatus(null);

      try {
        const response = await fetch(`${FASTAPI_URL}/config/siteflow/user/${user._id}`, {
          headers: { Authorization: `Bearer ${user.token}` },
        });

        if (!response.ok) {
          if (response.status === 404) {
            setSiteflowConfig({ server_url: '', client_id: '', client_secret: '' });
            setIsLoadingConfig(false);
            return;
          }

          const errorData = await response.json().catch(() => ({ detail: 'Failed to fetch Siteflow config' }));
          throw new Error(errorData.detail || `HTTP error ${response.status}`);
        }

        const data = await response.json();
        setSiteflowConfig({
          server_url: data.SITEFLOW_SERVER_URL || '',
          client_id: data.SITEFLOW_CLIENT_ID || '',
          client_secret: data.SITEFLOW_CLIENT_SECRET || '',
        });
      } catch (error) {
        Sentry.captureException(error, {
          tags: { flow: 'siteflow-api', stage: 'fetch-config' },
        });
        console.error('Error fetching Siteflow config:', error);
        setConfigError(error instanceof Error ? error.message : 'An unknown error occurred');
      } finally {
        setIsLoadingConfig(false);
      }
    };

    fetchSiteflowConfig();
  }, [user]);

  const handleConfigChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setSiteflowConfig((prev) => ({ ...prev, [name]: value }));
    setSaveStatus(null);
  };

  const handleSaveSiteflowConfig = async () => {
    if (!user?.token || !user?._id) {
      setSaveStatus('Error: Not authenticated');
      setConfigError('You must be logged in to save the configuration.');
      return;
    }

    setSaveStatus('Saving...');
    setConfigError(null);

    const configToSave = {
      SITEFLOW_SERVER_URL: siteflowConfig.server_url || null,
      SITEFLOW_CLIENT_ID: siteflowConfig.client_id || null,
      SITEFLOW_CLIENT_SECRET: siteflowConfig.client_secret || null,
    };

    try {
      const response = await fetch(`${FASTAPI_URL}/config/siteflow/user/${user._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user.token}`,
        },
        body: JSON.stringify(configToSave),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Failed to save config' }));
        throw new Error(errorData.detail || `HTTP error ${response.status}`);
      }

      setSaveStatus('Configuration saved successfully!');
    } catch (error) {
      Sentry.captureException(error, {
        tags: { flow: 'siteflow-api', stage: 'save-config' },
      });
      console.error('Error saving Siteflow config:', error);
      const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred';
      setConfigError(`Failed to save: ${errorMessage}`);
      setSaveStatus('Save failed');
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Your Siteflow API Configuration</CardTitle>
        <CardDescription>
          {!user?._id
            ? 'Please log in to manage your Siteflow API settings.'
            : 'Configure your Siteflow API credentials. Project and family are selected when sending each document.'}
          {isLoadingConfig && <p className="text-sm text-muted-foreground">Loading configuration...</p>}
          {configError && !isLoadingConfig && <p className="text-sm text-red-500">Error: {configError}</p>}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="server_url">Server URL</Label>
            <Input
              id="server_url"
              name="server_url"
              value={siteflowConfig.server_url ?? ''}
              onChange={handleConfigChange}
              placeholder="https://your-instance.siteflow.co"
              disabled={isLoadingConfig || !user?._id}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="client_id">Client ID</Label>
            <Input
              id="client_id"
              name="client_id"
              value={siteflowConfig.client_id ?? ''}
              onChange={handleConfigChange}
              placeholder="Your Siteflow Client ID"
              disabled={isLoadingConfig || !user?._id}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="client_secret">Client Secret</Label>
            <Input
              id="client_secret"
              name="client_secret"
              type="password"
              value={siteflowConfig.client_secret ?? ''}
              onChange={handleConfigChange}
              placeholder="Your Siteflow Client Secret"
              disabled={isLoadingConfig || !user?._id}
            />
          </div>
        </div>
        {configError && <p className="text-sm text-red-500 pt-2">{configError}</p>}
      </CardContent>
      <CardFooter className="flex justify-between items-center">
        {saveStatus && (
          <p
            className={`text-sm ${
              saveStatus.startsWith('Error') || saveStatus === 'Save failed'
                ? 'text-red-500'
                : 'text-green-600'
            }`}
          >
            {saveStatus}
          </p>
        )}
        {!saveStatus && <div />}
        <Button
          onClick={handleSaveSiteflowConfig}
          disabled={isLoadingConfig || saveStatus === 'Saving...' || !user?._id}
        >
          {saveStatus === 'Saving...' ? 'Saving...' : 'Save Configuration'}
        </Button>
      </CardFooter>
    </Card>
  );
};

export default SiteflowConfig;
